import React, { useState, useMemo, useCallback } from 'react'
import { useEffect } from 'react'

import useDataLayer from 'src/hooks/useDataLayer/dataLayerBody'
import CategoryHeader from 'src/components/CategoryHeader'
import useDevice from 'src/hooks/useDevice'
import { Link } from 'gatsby'

import { GridContainer, GridCard } from './style'
import SpotlightNewsSmall from '../SpotlightNewsSmall'
import { CardProps } from '../SpotlightNewsSmall/types'

type DataGridType = {
  image: string
  slug: string
  title?: string
  gridArea?: string
  link?: string
  isFromInset?: boolean
  subcategorySlug?: string
  categorySlug?: string
  insetCategory?: string
  subCategory?: string
}

type TemplateElementType = {
  gridArea: string
}

type TemplateDeviceType = {
  limit: number
  elements: TemplateElementType[]
}

type DeviceKeys = 'mobile' | 'tablet' | 'desktopLG' | 'desktopXL'

type TemplateDataType = {
  rows: number
  mobile: TemplateDeviceType
  tablet: TemplateDeviceType
  desktopLG: TemplateDeviceType
  desktopXL: TemplateDeviceType
}

type TemplatesType = {
  [key: string]: TemplateDataType
}

type SpotlightImageProps = {
  alt: string
  url: string
}

type SpotlightNewsSmallItem = {
  title: string
  link: string
  image: SpotlightImageProps
  video: boolean
  isFromInset?: boolean
  insetCategory?: string
}

export type GridType = {
  showCategoryHeader?: boolean
  category: string
  categoryLabel: string
  slug: string
  template: string
  dados: (DataGridType | CardProps)[]
  removeGradient?: boolean
  isCategoryTemplate?: boolean
}

function getNumber(string: string): number {
  const number = string.replace(/\D/g, '')
  return Number(number)
}

const defaultStyles = {
  large: 'font-size: 32px; line-height: 40px;',
  medium: 'font-size: 32px; line-height: 40px;',
  small: 'font-size: 20px; line-height: 25px;',
}

function getStylesCard(gridArea: string): string {
  const [, , row, column] = gridArea.split('/')
  if (getNumber(column) === 3) {
    return defaultStyles.large
  }
  if (getNumber(row) === 2 && getNumber(column) === 2) {
    return defaultStyles.medium
  }
  return defaultStyles.small
}

export const templates: TemplatesType = {
  A: {
    rows: 2,
    mobile: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 4',
        },
        {
          gridArea: '2 / 1 / span 1 / span 4',
        },
        {
          gridArea: '3 / 1 / span 1 / span 4 ',
        },
      ],
    },
    tablet: {
      limit: 2,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 2 / span 2',
        },
      ],
    },
    desktopLG: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 2 / span 1',
        },
        {
          gridArea: '1 / 4 / span 2 / span 1 ',
        },
      ],
    },
    desktopXL: {
      limit: 1,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 4',
        },
      ],
    },
  },
  B: {
    rows: 2,
    mobile: {
      limit: 2,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 4',
        },
        {
          gridArea: '2 / 1 / span 1 / span 4',
        },
      ],
    },
    tablet: {
      limit: 1,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 4',
        },
      ],
    },
    desktopLG: {
      limit: 2,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 2 / span 2',
        },
      ],
    },
    desktopXL: {
      limit: 2,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 2 / span 2',
        },
      ],
    },
  },
  C: {
    rows: 2,
    mobile: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 4',
        },
        {
          gridArea: '2 / 1 / span 1 / span 4',
        },
        {
          gridArea: '3 / 1 / span 1 / span 4 ',
        },
      ],
    },
    tablet: {
      limit: 2,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 1 / span 2',
        },
        {
          gridArea: '2 / 3 / span 1 / span 2 ',
        },
      ],
    },
    desktopLG: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 2 / span 1',
        },
        {
          gridArea: '1 / 4 / span 2 / span 1 ',
        },
      ],
    },
    desktopXL: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 2 / span 1 ',
        },
        {
          gridArea: '1 / 4 / span 2 / span 1 ',
        },
      ],
    },
  },
  D: {
    rows: 2,
    mobile: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 4',
        },
        {
          gridArea: '2 / 1 / span 1 / span 4',
        },
        {
          gridArea: '3 / 1 / span 1 / span 4 ',
        },
      ],
    },
    tablet: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 1 / span 2',
        },
        {
          gridArea: '2 / 3 / span 1 / span 2',
        },
      ],
    },
    desktopLG: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 1 / span 2',
        },
        {
          gridArea: '2 / 3 / span 1 / span 2 ',
        },
      ],
    },
    desktopXL: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 1 / span 2',
        },
        {
          gridArea: '2 / 3 / span 1 / span 2 ',
        },
      ],
    },
  },

  E: {
    rows: 1,
    mobile: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 4',
        },
        {
          gridArea: '2 / 1 / span 1 / span 4',
        },
        {
          gridArea: '3 / 1 / span 1 / span 4 ',
        },
      ],
    },
    tablet: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 1',
        },
        {
          gridArea: '1 / 2 / span 1 / span 1',
        },
        {
          gridArea: '1 / 3 / span 1 / span 1',
        },
      ],
    },
    desktopLG: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 1',
        },
        {
          gridArea: '1 / 2 / span 1 / span 1',
        },
        {
          gridArea: '1 / 3 / span 1 / span 1',
        },
      ],
    },
    desktopXL: {
      limit: 4,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 1',
        },
        {
          gridArea: '1 / 2 / span 1 / span 1',
        },
        {
          gridArea: '1 / 3 / span 1 / span 1',
        },
        {
          gridArea: '1 / 4 / span 1 / span 1',
        },
      ],
    },
  },
  F: {
    rows: 2,
    mobile: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 1 / span 4',
        },
        {
          gridArea: '2 / 1 / span 1 / span 4',
        },
        {
          gridArea: '3 / 1 / span 1 / span 4 ',
        },
      ],
    },
    tablet: {
      limit: 2,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 2 / span 2',
        },
      ],
    },
    desktopLG: {
      limit: 3,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '1 / 3 / span 2 / span 1',
        },
        {
          gridArea: '1 / 4 / span 2 / span 1 ',
        },
      ],
    },
    desktopXL: {
      limit: 4,
      elements: [
        {
          gridArea: '1 / 1 / span 2 / span 2',
        },
        {
          gridArea: '2 / 3 / span 1 / span 1',
        },
        {
          gridArea: '1 / 3 / span 1 / span 1 ',
        },
        {
          gridArea: '1 / 4 / span 2 / span 1 ',
        },
      ],
    },
  },
}

const Grid = ({
  showCategoryHeader,
  category,
  categoryLabel,
  template,
  dados,
  removeGradient,
  isCategoryTemplate,
}: GridType) => {
  const device = useDevice()
  const [sendDatalayerEvent] = useDataLayer()
  const [templateInformation, setTemplateInformation] = useState<TemplateDeviceType | null>(null)

  const templateGrid = useMemo(() => {
    if (template && template in templates) {
      return templates[template as keyof TemplatesType]
    }
    return null
  }, [template])

  const getGridArea = useCallback(
    (position: number): TemplateElementType | null => {
      if (!templateInformation) {
        return null
      }
      return templateInformation.elements?.[position] || null
    },
    [templateInformation],
  )

  const isOneRow = useCallback((): boolean => templateGrid?.rows === 1, [templateGrid])

  useEffect(() => {
    if (!device) {
      return
    }
    const getTemplateInfo = (deviceData: string): TemplateDeviceType | null => {
      if (!templateGrid) {
        return null
      }
      const deviceKey = deviceData as DeviceKeys
      if (deviceKey in templateGrid) {
        return templateGrid[deviceKey]
      }
      return null
    }
    setTemplateInformation(getTemplateInfo(device))
  }, [device, templateGrid])

  const filteredCards = useMemo(() => {
    if (!templateInformation) {
      return []
    }

    return dados.reduce<React.ReactElement[]>((all, item, index) => {
      if (templateInformation.limit < index + 1) {
        return all
      }

      const gridAreaInfo = getGridArea(index)
      if (!gridAreaInfo) {
        return all
      }

      const { gridArea } = gridAreaInfo
      const stylesCard = getStylesCard(gridArea)

      const dataItem = item as DataGridType

      const getLinkUrl = (): string => {
        if (dataItem.link) {
          return dataItem.link
        }
        if (isCategoryTemplate && !dataItem.isFromInset) {
          return `/${dataItem.subcategorySlug}/${item.slug}`
        }
        if (!isCategoryTemplate && !dataItem.isFromInset) {
          return `/${dataItem.categorySlug}/${dataItem.subcategorySlug}/${item.slug}`
        }
        if (dataItem.isFromInset) {
          return `https://www.inset.com.br/${dataItem.insetCategory}/${item.slug}`
        }
        return ''
      }

      const linkUrl = getLinkUrl()
      const isExternalLink = dataItem.isFromInset

      return [
        ...all,
        <GridCard
          key={item.slug ? `${item.slug}-${index}` : `grid-${index}`}
          gridArea={gridArea}
          backGroundImage={item.image}
          className={`${isOneRow() ? 'cardOneRow' : ''} box`}
          styles={stylesCard}
          removeGradient={removeGradient}
          primary=""
        >
          <Link
            to={linkUrl}
            target={isExternalLink ? '_blank' : '_self'}
            rel={isExternalLink ? 'noreferrer' : ''}
            className="w-100 h-100 d-flex"
            onClick={() => {
              sendDatalayerEvent({
                section: 'Melhores conteúdos',
                element_action: 'click button',
                element_name: item.title || '',
                redirect_url: linkUrl,
                section_name: 'Melhores conteúdos',
              })
            }}
          >
            <div className="image-area">
              <img loading="lazy" src={item.image} alt="" className="w-100 h-100" />
            </div>
            {dataItem.subCategory && <span className="category-label">{dataItem.subCategory}</span>}
            <div className="text-area">
              {item.title && (
                <div>
                  <h3
                    className={`fs-24 lh-30 ${
                      index === 0 ? 'fs-xl-40 lh-xl-50' : 'fs-xl-20 lh-xl-25 mb-0'
                    } d-block`}
                  >
                    {item.title}
                  </h3>
                  {index === 0 && (
                    <p className="fs-14 lh-17 fs-xl-16 lh-xl-19 mb-0">{item.title}</p>
                  )}
                </div>
              )}
            </div>
          </Link>
        </GridCard>,
      ]
    }, [])
  }, [
    templateInformation,
    dados,
    isCategoryTemplate,
    removeGradient,
    sendDatalayerEvent,
    getGridArea,
    isOneRow,
  ])

  if (!templateInformation) {
    return <div>Não foi possível achar o template</div>
  }

  const transformDataForSpotlight = (): SpotlightNewsSmallItem[] =>
    dados.map((item) => {
      const dataItem = item as DataGridType
      return {
        title: item.title || '',
        link: dataItem.link || '',
        image: {
          alt: item.title || '',
          url: item.image,
        },
        video: false,
        isFromInset: dataItem.isFromInset,
        insetCategory: dataItem.insetCategory,
      }
    })
  return (
    <>
      {isOneRow() ? (
        <SpotlightNewsSmall data={transformDataForSpotlight()} />
      ) : (
        <>
          {showCategoryHeader && (
            <CategoryHeader
              category={categoryLabel}
              categoryLink={`/${category}`}
              className="mb-3"
            />
          )}
          <GridContainer gridRows={templateGrid?.rows || 1}>
            <div className="grid-column-style">{filteredCards}</div>
          </GridContainer>
        </>
      )}
    </>
  )
}

export default Grid
